import React, { useCallback, useMemo, useState } from "react";
import "../../assets/scss/roulette/roulette-kh.scss";
import swal from "sweetalert";
import moment from "moment";

import { env } from "../../env";
import LoadingGif from "../../utils/LoadingGif";
import BettingBoard from "./BettingBoard";
import History from "./History";
import LeftSide from "./LeftSide";
import CountDownTimer from "./CountDownTimer";

import DatetimeContainer from "../date/DatetimeContainer";
import BettingDialog from "../lotto5/BettingDialog";
import RouletteWheel from "./RouletteWheel";

const initialBetState = {
    showDeleteBetting: false,
    showBetting: false,
    betCode: "",
    betAmount: 0,
    betGameType: "",
    betGameOdds: "",
    betGameRange: "",
    betGameTime: "",
    betMessage: "",
    isShowValidateBalance: false,
    messageValidateBalance: null,
    isEnableBetNow: true,
};

const RouletteLayout = (props) => {
    const {
        t,
        config,
        user,
        draw,
        newResult,
        isStopBet,
        addRouletteBetting,
        rouletteConfirmBettingNow,
        fetchRouletteRecentBetReport,
        fetchUserBalance,
        fetchRouletteDrawingResult,
        fetchRouletteListBetting,
        fetchUserBalanceValidate,
        deleteRouletteBetting,
    } = props;

    const countDownTimer = draw?.countDownTimer ?? 0;
    const totalResult = draw?.totalResult ?? 0;
    const drawCode =
        draw?.newResult?.drawCode ?? draw?.oldResult?.drawCode ?? "";


    const [betState, setBetState] = useState(initialBetState);

    const {
        showBetting,
        betCode,
        betAmount,
        betGameType,
        betGameOdds,
        betGameRange,
        betGameTime,
        betMessage,
        isShowValidateBalance,
        messageValidateBalance,
        isEnableBetNow,
    } = betState;

    const lastResult = draw?.lastResult || {};
    const {
        drawLabel,
        underOverCode,
        oddEvenCode,
        colorCode,
        result,
    } = lastResult;

    const resetBetForm = useCallback(() => {
        setBetState((prev) => ({
            ...prev,
            showBetting: false,
            betCode: "",
            betAmount: 0,
            betGameType: "",
            betGameOdds: "",
            betGameRange: "",
            betGameTime: "",
            betMessage: "",
        }));
    }, []);

    const onCancelBetting = useCallback(() => {
        resetBetForm();
    }, [resetBetForm]);

    const clickAmountBetBtn = useCallback((amount) => {
        setBetState((prev) => ({
            ...prev,
            betAmount: parseInt(prev.betAmount || 0, 10) + amount,
        }));
    }, []);

    const resetDefaultAmount = useCallback(() => {
        setBetState((prev) => ({
            ...prev,
            betAmount: 0,
        }));
    }, []);

    const changeBetAmount = useCallback((e) => {
        const value = e.target.value;

        if (value >= 0 || !isNaN(value) || value === undefined) {
            setBetState((prev) => ({
                ...prev,
                betAmount: value,
            }));
        }
    }, []);

    const isValidBetAmount = useCallback(
        (amount = 0) => {
            const parsedAmount = parseInt(amount, 10);

            if (Number.isNaN(parsedAmount)) {
                return false;
            }

            const { maxBet, minBet } = config.limit;
            return !(parsedAmount < minBet || parsedAmount > maxBet);
        },
        [config]
    );

    const submitBetNow = useCallback(
        (isPrint) => {
            if (!isEnableBetNow) {
                return;
            }

            rouletteConfirmBettingNow(() => {
                setBetState((prev) => ({
                    ...prev,
                    isEnableBetNow: false,
                }));
            })
                .then(() => {
                    fetchRouletteRecentBetReport().then(() => {
                        if (isPrint) {
                            const printButton = document.getElementById("print-button");
                            if (printButton) {
                                printButton.click();
                            }
                        }
                    });

                    fetchUserBalance();

                    setBetState((prev) => ({
                        ...prev,
                        isEnableBetNow: true,
                    }));
                })
                .catch((error) => {
                    setBetState((prev) => ({
                        ...prev,
                        isEnableBetNow: true,
                    }));

                    const message =
                        error?.response?.data?.message || error?.message || "Bet failed";

                    swal(message, { icon: "error", button: "បិទ" });
                });
        },
        [
            isEnableBetNow,
            rouletteConfirmBettingNow,
            fetchRouletteRecentBetReport,
            fetchUserBalance,
        ]
    );

    const onConfirmBetting = useCallback(
        async (logs) => {
            const { userBalance, isAddToCart, isAutoPrint } = user;

            const totalCartAmount = fetchRouletteListBetting({ isClear: false }) || 0;
            const parsedBetAmount = parseInt(betAmount || 0, 10);

            if (!isValidBetAmount(parsedBetAmount)) {
                setBetState((prev) => ({
                    ...prev,
                    betMessage: t("labels:keno.requireAmount"),
                }));
                return;
            }

            if (totalCartAmount + parsedBetAmount > userBalance.balance) {
                setBetState((prev) => ({
                    ...prev,
                    betMessage: t("labels:common.NOT_ENOUGH_BALANCE"),
                }));
                return;
            }

            const data = {
                drawCode: newResult?.drawCode,
                betTypeCode: betCode,
                amount: parsedBetAmount,
                betLog: logs,
                rebateRate: betGameOdds,
            };

            await addRouletteBetting(data);

            if (!isAddToCart) {
                submitBetNow(isAutoPrint);
            }

            setBetState((prev) => ({
                ...prev,
                showBetting: false,
                betAmount: 0,
                betGameType: "",
                betGameOdds: "",
                betCode: "",
                betGameRange: "",
                betGameTime: "",
                betMessage: null,
            }));
        },
        [
            user,
            fetchRouletteListBetting,
            betAmount,
            isValidBetAmount,
            t,
            newResult,
            betCode,
            betGameOdds,
            addRouletteBetting,
            submitBetNow,
        ]
    );

    const afterPrint = useCallback(() => {
        fetchRouletteDrawingResult();
    }, [fetchRouletteDrawingResult]);

    const onBetting = useCallback(
        (item) => {
            if (isStopBet) {
                return;
            }

            fetchUserBalanceValidate()
                .then(() => {
                    setBetState((prev) => ({
                        ...prev,
                        showBetting: true,
                        betGameType: item.name,
                        betGameOdds: item.value,
                        betGameRange: item.resultRange,
                        betCode: item.code,
                        betGameTime: moment().format("HH:mm:ss"),
                        betMessage: "",
                        isShowValidateBalance: false,
                        messageValidateBalance: null,
                    }));
                })
                .catch((error) => {
                    setBetState((prev) => ({
                        ...prev,
                        isShowValidateBalance: true,
                        messageValidateBalance: t(
                            "labels:common." + error?.response?.data?.message
                        ),
                    }));
                });
        },
        [isStopBet, fetchUserBalanceValidate, t]
    );

    const showConfirmDelete = useCallback(
        (id) => {
            deleteRouletteBetting({ id });
        },
        [deleteRouletteBetting]
    );

    const headerResultText = useMemo(() => {
        if (!(underOverCode && oddEvenCode)) {
            return "";
        }

        return (
            t("labels:betPrefix." + underOverCode) +
            "  /   " +
            t("labels:betPrefix." + oddEvenCode) +
            "  /   " +
            t("labels:betPrefix." + colorCode) +
            "  /   " +
            result
        );
    }, [underOverCode, oddEvenCode, colorCode, result, t]);



    return (
        <div className="layout-wrapper">
            <div className="sidebar-wrapper">
                <LeftSide
                    afterPrint={afterPrint}
                    showConfirmDelete={showConfirmDelete}
                    isEnableBetNow={isEnableBetNow}
                    submitBetNow={submitBetNow}
                    {...props}
                />
            </div>

            <div className="r-header">
                <div
                    className={`roulette-header platform-${env.PLATFORM_FOR} block-header d-flex justify-content-between align-items-center h-100`}
                >
                    <div
                        className="d-flex justify-content-between align-items-center text-black"
                        style={{ width: "50%" }}
                    >
                        <div className="px-lg-4 mr-4 mr-lg-0 label-header">
                            {drawLabel}
                        </div>

                        {headerResultText ? (
                            <div className="px-lg-4 mr-4 mr-lg-0 label-header">
                                {headerResultText}
                            </div>
                        ) : (
                            <div className="px-lg-4 mr-4 mr-lg-0 label-header" />
                        )}

                        <div className="px-lg-4 mr-4 mr-lg-0 label-header">
                            <LoadingGif className="w-load-roulette" />
                            <span className="label-header text-black">
                {newResult !== null
                    ? newResult?.drawLabel
                    : t("labels:upcoming_draw")}
              </span>
                        </div>
                    </div>

                    <div className="px-lg-3">
                        <div className="mr-4 mr-lg-0 label-header text-black">
                            <DatetimeContainer />
                        </div>
                    </div>
                </div>
            </div>

            <div className="wheel-container position-relative wheel-wrap">
                <div className="wheel d-flex align-items-center" style={{ height: "90%" }}>
                    <RouletteWheel {...props} />
                </div>

                <CountDownTimer
                    countDownTimer={countDownTimer}
                    totalResult={totalResult}
                    drawCode={drawCode}
                    onLastTenSecond={props.onLastTenSecond}
                    onCancelBetting={onCancelBetting}
                />

            </div>

            <BettingBoard {...props} onBetting={onBetting} />

            <History {...props} />

            <BettingDialog
                {...props}
                changeBetAmount={changeBetAmount}
                clickAmountBetBtn={clickAmountBetBtn}
                onConfirmBettingLotto5={onConfirmBetting}
                resetDefaultAmount={resetDefaultAmount}
                onCancelBetting={onCancelBetting}
                isRoulette
                showBetting={showBetting}
                betCode={betCode}
                betAmount={betAmount}
                betGameType={betGameType}
                betGameOdds={betGameOdds}
                betGameRange={betGameRange}
                betGameTime={betGameTime}
                betMessage={betMessage}
                isShowValidateBalance={isShowValidateBalance}
                messageValidateBalance={messageValidateBalance}
                isEnableBetNow={isEnableBetNow}
            />
        </div>
    );
};

export default RouletteLayout;